import { ArrowDown, ArrowUp, Columns2, Pencil, Trash2 } from "lucide-react";

export const TYPE_META = {
  start: { label: "Start", shape: "rounded-full", cls: "bg-emerald-50 border-emerald-300 text-emerald-900" },
  step: { label: "Step", shape: "rounded-lg", cls: "bg-card border-border" },
  decision: { label: "Decision", shape: "rounded-lg", cls: "bg-amber-50 border-amber-300 text-amber-900" },
  end: { label: "End", shape: "rounded-full", cls: "bg-slate-100 border-slate-300 text-slate-800" },
};

const Stub = () => <div className="h-3 w-px bg-muted-foreground/40" />;
const FlowArrow = () => <ArrowDown className="h-6 w-6 my-1 text-muted-foreground" />;

const LinkedChips = ({ ids, taskById, tiny }) => {
  const linked = (ids || []).map((id) => taskById[id]).filter(Boolean);
  if (!linked.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${tiny ? "mt-1.5" : "mt-2"}`}>
      {linked.map((t) => <span key={t.id} className={`${tiny ? "text-[9px]" : "text-[10px]"} px-1.5 py-0.5 rounded bg-background/70 border border-border`}>{t.title}</span>)}
    </div>
  );
};

function Controls({ s, i, editors, floating }) {
  if (!editors) return null;
  const parAllowed = s.type === "step" && (s.parallel_with_prev || editors.canParallel(i));
  const wrap = floating
    ? "absolute -right-2 top-1/2 -translate-y-1/2 translate-x-full flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
    : "flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0";
  return (
    <div className={wrap}>
      <button className="p-1 rounded hover:bg-secondary" onClick={() => editors.move(i, -1)} title="Move up" data-testid={`step-up-${s.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
      <button className="p-1 rounded hover:bg-secondary" onClick={() => editors.move(i, 1)} title="Move down" data-testid={`step-down-${s.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      {parAllowed && (
        <button className={`p-1 rounded ${s.parallel_with_prev ? "bg-primary/15 text-primary" : "hover:bg-secondary"}`}
          onClick={() => editors.toggleParallel(i)}
          title={s.parallel_with_prev ? "Back to sequence — run after the previous step" : "Run side-by-side (in parallel) with the previous step"}
          data-testid={`step-parallel-${s.id}`}><Columns2 className="h-3.5 w-3.5" /></button>
      )}
      <button className="p-1 rounded hover:bg-secondary" onClick={() => editors.edit(s)} title="Edit" data-testid={`step-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
      <button className="p-1 rounded hover:bg-secondary" onClick={() => editors.remove(i)} title="Delete" data-testid={`step-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function StepBox({ s, i, taskById, editors }) {
  const meta = TYPE_META[s.type] || TYPE_META.step;
  const draggable = !!editors && s.type === "step";
  return (
    <div className={`group relative w-full border-2 px-5 py-3.5 ${meta.shape} ${meta.cls} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      draggable={draggable}
      onDragStart={draggable ? (e) => e.dataTransfer.setData("text/step-index", String(i)) : undefined}
      onDragOver={draggable ? (e) => e.preventDefault() : undefined}
      onDrop={draggable ? (e) => { e.preventDefault(); const from = parseInt(e.dataTransfer.getData("text/step-index"), 10); if (!Number.isNaN(from) && from !== i) editors.dropParallel(from, i); } : undefined}
      title={draggable ? "Drag onto another step to run them side-by-side (in parallel)" : undefined}
      data-testid={`process-step-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-60">{meta.label}{s.owner ? ` · ${s.owner}` : ""}</p>
          <p className="font-semibold text-sm mt-0.5">{s.title || "Untitled step"}</p>
          {s.description && <p className="text-xs opacity-75 mt-1 whitespace-pre-wrap">{s.description}</p>}
          <LinkedChips ids={s.linked_task_ids} taskById={taskById} />
        </div>
        <Controls s={s} i={i} editors={editors} />
      </div>
    </div>
  );
}

function BranchColumn({ label, stepId, items, taskById, editors }) {
  const yes = label === "Yes";
  const head = yes ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-rose-100 text-rose-800 border-rose-300";
  const card = yes ? "bg-emerald-50/70 border-emerald-200" : "bg-rose-50/70 border-rose-200";
  const branch = yes ? "yes_steps" : "no_steps";
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={`branch-${label.toLowerCase()}-${stepId}`}>
      <div className="h-4 w-px bg-border" />
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${head}`}>{yes ? "✓ Yes" : "✗ No"}</span>
      {items.map((b, idx) => (
        <div key={b.id} className="flex flex-col items-center w-full">
          <div className="h-3 w-px bg-border" />
          <div className={`group relative w-full border rounded-lg px-3 py-2 ${card}`} data-testid={`branch-step-${b.id}`}>
            <p className="text-[9px] font-bold uppercase tracking-wider opacity-60">{label} path{b.owner ? ` · ${b.owner}` : ""}</p>
            <p className="font-semibold text-xs mt-0.5">{b.title}</p>
            {b.description && <p className="text-[11px] opacity-75 mt-0.5 whitespace-pre-wrap">{b.description}</p>}
            <LinkedChips ids={b.linked_task_ids} taskById={taskById} tiny />
            {editors && (
              <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 rounded">
                <button className="p-0.5 rounded hover:bg-secondary" onClick={() => editors.branchMove(stepId, branch, idx, -1)} title="Move up"><ArrowUp className="h-3 w-3" /></button>
                <button className="p-0.5 rounded hover:bg-secondary" onClick={() => editors.branchMove(stepId, branch, idx, 1)} title="Move down"><ArrowDown className="h-3 w-3" /></button>
                <button className="p-0.5 rounded hover:bg-secondary" onClick={() => editors.branchEdit(stepId, branch, b)} title="Edit" data-testid={`branch-edit-${b.id}`}><Pencil className="h-3 w-3" /></button>
                <button className="p-0.5 rounded hover:bg-secondary" onClick={() => editors.branchRemove(stepId, branch, b.id)} title="Delete" data-testid={`branch-delete-${b.id}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            )}
          </div>
        </div>
      ))}
      {editors && (
        <button onClick={() => editors.branchAdd(stepId, branch)}
          className={`mt-1 w-full text-[11px] font-semibold border border-dashed rounded-lg py-1.5 transition-colors ${yes ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}
          data-testid={`branch-add-${label.toLowerCase()}-${stepId}`}>
          + Add {label} step
        </button>
      )}
    </div>
  );
}

function DecisionRow({ s, i, taskById, editors }) {
  return (
    <>
      <div className="group relative flex flex-col items-center py-3">
        <div className="w-44 h-44 rotate-45 border-2 border-amber-400 bg-amber-50 rounded-xl shadow-sm flex items-center justify-center" data-testid={`process-step-${s.id}`}>
          <div className="-rotate-45 w-44 text-center px-3">
            <p className="text-[9px] font-bold uppercase tracking-wider text-amber-700/70">Decision{s.owner ? ` · ${s.owner}` : ""}</p>
            <p className="font-semibold text-xs text-amber-900 leading-snug line-clamp-3 mt-0.5">{s.title || "Untitled decision"}</p>
            {s.description && <p className="text-[10px] text-amber-800/70 line-clamp-2 mt-1">{s.description}</p>}
          </div>
        </div>
        <Controls s={s} i={i} editors={editors} floating />
      </div>
      {((s.yes_steps || []).length > 0 || (s.no_steps || []).length > 0 || editors) && (
        <div className="grid grid-cols-2 gap-5 w-full mt-1" data-testid={`decision-branches-${s.id}`}>
          <BranchColumn label="Yes" stepId={s.id} items={s.yes_steps || []} taskById={taskById} editors={editors} />
          <BranchColumn label="No" stepId={s.id} items={s.no_steps || []} taskById={taskById} editors={editors} />
        </div>
      )}
    </>
  );
}

export default function ProcessFlowCanvas({ steps, taskById = {}, editors = null }) {
  const rows = [];
  steps.forEach((s, i) => {
    const last = rows[rows.length - 1];
    if (s.type === "step" && s.parallel_with_prev && last?.kind === "steps"
        && last.items[last.items.length - 1].s.type === "step" && last.items.length < 3) {
      last.items.push({ s, i });
      return;
    }
    if (s.type === "decision") rows.push({ kind: "decision", s, i });
    else rows.push({ kind: "steps", items: [{ s, i }] });
  });

  return (
    <div className="flex flex-col items-center gap-0 py-4" data-testid="process-flow">
      {steps.length === 0 && <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg px-6 py-8">No steps yet — click "Add step" to start building the flow.</p>}
      {rows.map((row, ri) => {
        const notLast = ri < rows.length - 1;
        if (row.kind === "decision") {
          return (
            <div key={row.s.id} className="flex flex-col items-center w-full max-w-2xl">
              <DecisionRow s={row.s} i={row.i} taskById={taskById} editors={editors} />
              {notLast && <FlowArrow />}
            </div>
          );
        }
        if (row.items.length === 1) {
          const { s, i } = row.items[0];
          return (
            <div key={s.id} className="flex flex-col items-center w-full max-w-xl">
              <StepBox s={s} i={i} taskById={taskById} editors={editors} />
              {notLast && <FlowArrow />}
            </div>
          );
        }
        return (
          <div key={row.items[0].s.id} className="flex flex-col items-center w-full max-w-4xl" data-testid={`parallel-row-${row.items[0].s.id}`}>
            <Stub />
            <div className="relative w-[72%] border-t-2 border-muted-foreground/25">
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-background px-2 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">In parallel</span>
            </div>
            <div className="flex w-full gap-4 items-stretch justify-center px-2">
              {row.items.map(({ s, i }) => (
                <div key={s.id} className="flex-1 min-w-0 max-w-sm flex flex-col items-center">
                  <Stub />
                  <StepBox s={s} i={i} taskById={taskById} editors={editors} />
                  <div className="flex-1" />
                  <Stub />
                </div>
              ))}
            </div>
            <div className="w-[72%] border-t-2 border-muted-foreground/25" />
            {notLast && <FlowArrow />}
          </div>
        );
      })}
    </div>
  );
}
